// Whether a picked image actually is a QR code. The payment QR is printed on
// every invoice for a client to pay against, so a screenshot of the wrong
// thing is not a cosmetic mistake — it is an invoice nobody can pay.

import jsQR from "jsqr";

/**
 * `not-found` is a verdict about the picture; `unavailable` is a verdict about
 * the browser. They are kept apart because only the first is the user's to fix
 * — a canvas that will not give up its pixels is no reason to refuse a file.
 */
export type QrScan = "found" | "not-found" | "unavailable";

/**
 * First pass, in pixels on the longest side. A payment QR is a few hundred
 * pixels of modules however large the file around it is, so this finds nearly
 * all of them for a fraction of the work of a full-size read.
 */
const FIRST_PASS_MAX_SIDE = 1024;

/** The image's pixels at `side` or under, or `null` if canvas will not say. */
const readPixels = (
  image: HTMLImageElement,
  side: number
): ImageData | null => {
  const longest = Math.max(image.naturalWidth, image.naturalHeight);
  const scale = longest > side ? side / longest : 1;
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  try {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;

    context.drawImage(image, 0, 0, width, height);
    return context.getImageData(0, 0, width, height);
  } catch {
    // A tainted canvas, or no canvas at all. Either way there are no pixels to
    // judge, which is not the same as there being no QR code in them.
    return null;
  }
};

/**
 * Looks for a QR code in an already-decoded image.
 *
 * Two passes, cheap one first: a downscale that catches the ordinary case — a
 * QR saved from a payment app — and then the native resolution, which is what
 * rescues a photographed or unusually dense code that the downscale blurred
 * past. Only a picture that survives both is called `not-found`, because the
 * cost of the two readings is a moment and the cost of a false refusal is a
 * user who cannot get past the form.
 */
export const scanForQrCode = (image: HTMLImageElement): QrScan => {
  const longest = Math.max(image.naturalWidth, image.naturalHeight);
  const sides =
    longest > FIRST_PASS_MAX_SIDE ? [FIRST_PASS_MAX_SIDE, longest] : [longest];

  // Whether any pass got far enough for its silence to mean anything.
  let conclusive = false;

  for (const side of sides) {
    const pixels = readPixels(image, side);
    if (!pixels) continue;
    conclusive = true;

    try {
      // `attemptBoth` covers the light-on-dark codes that a dark-mode
      // screenshot produces, which are as valid as any other and would read as
      // blank without it.
      const found = jsQR(pixels.data, pixels.width, pixels.height, {
        inversionAttempts: "attemptBoth",
      });
      if (found) return "found";
    } catch {
      // A decoder that fell over has not shown the picture to be innocent of a
      // QR code, so this pass is discounted rather than counted as a refusal.
      conclusive = false;
    }
  }

  return conclusive ? "not-found" : "unavailable";
};
